import { TabularTypeAdapter } from 'app/model/adapters/dataflow/formatAdapters/tabular-type.adapter';
import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { AdxSettings } from 'app/model/dataflow/dataset-settings/adx-settings';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import {
    ArrayDataFlowType,
    IntegerDataFlowType,
    StringDataFlowType
} from 'worker/app-workers/shared/models/dataflow-datatype';
import { DataFlowAdapterUtils } from 'app/model/adapters/dataflow/adapter.util';
import { DatasetUtils, getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(AdxSettings)
})
export class AdxAdapter extends TabularTypeAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], _isSource: boolean, isInline: boolean, _datasetSettings: DataFlowDatasetSettings | undefined): void {
        if (linkedService) {
            const dbName = DatasetUtils.getDatabaseFromLinkedService(linkedService, 'database');
            if (dbName) {
                props.push(`database: ${DataFlowAdapterUtils.formatExpressionValue(dbName)}`);
            }
        }

        if (isInline || !dataset) {
            return;
        }

        props.push(`store: '${DatasetFormatType.ADX}'`);

        // Only add tableName for 'table' format (not for 'query')
        const formatProp = props.find((p) => p && p.substring(0, p.indexOf(':')) === 'format');
        const isTableFormat = formatProp?.includes('\'table\'');
        if (isTableFormat) {
            const tableName = getAttributeValueFromDataset(dataset, 'tableName');
            if (tableName) {
                props.push(`tableName: ${DataFlowAdapterUtils.formatExpressionValue(tableName)}`);
            }
        }
    }

    public async toSourceDSL(setting: AdxSettings): Promise<string[]> {
        const props = await super.toSourceDSL(setting);
        props.push(`store: '${DatasetFormatType.ADX}'`);
        if (setting.timeout.expression) {
            props.push(
                `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.timeout,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        const source = setting.owner as DataFlowSource;
        if (source.partitionOption === 'Partitioning' && source.partitionMethod === 'external') {
            if (source.useQueryPartition && source.partitionPredicates) {
                const validExps = source.partitionPredicates.filter(
                    (exp) => exp.expression != null && exp.expression !== ''
                );
                const result = validExps
                    .map((s) => `'${DataFlowExpressionUtils.escapeSingleQuoteInDslString(s.expression)}'`)
                    .join(',');
                if (result) {
                    props.push(`partitionPredicates: [${result}]`);
                }
            } else {
                if (source.partitionColumn) {
                    if (source.partitionColumn.dynamicPartitionColumn?.templateExpression) {
                        props.push(
                            `partitionColumn: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                source.partitionColumn.dynamicPartitionColumn,
                                StringDataFlowType.TYPE
                            )}`
                        );
                    } else {
                        props.push(`partitionColumn: '${source.partitionColumn.serializableName}'`);
                    }
                }
            }
        }
        return props;
    }

    public async toSinkDSL(setting: AdxSettings): Promise<string[]> {
        const props = await super.toSinkDSL(setting);
        props.push(`store: '${DatasetFormatType.ADX}'`);
        if (setting.timeout.expression) {
            props.push(
                `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.timeout,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        props.push(...DataFlowAdapterUtils.getPreSqlProps(setting));
        props.push(...DataFlowAdapterUtils.getPostSqlProps(setting));
        return props;
    }

    public async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<AdxSettings> {
        const setting = (await super.toSourceModel(owner, configs, handledConfigs)) as AdxSettings;
        if (configs.has('timeout')) {
            setting.timeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('timeout'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('timeout');
        }
        return setting;
    }

    public async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<AdxSettings> {
        const setting = (await super.toSinkModel(owner, configs, handledConfigs)) as AdxSettings;
        if (configs.has('timeout')) {
            setting.timeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('timeout'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('timeout');
        }
        if (configs.has('preSQLs')) {
            const expression = configs.get('preSQLs');
            if (expression.startsWith('[') && expression.endsWith(']')) {
                setting.isPreSqlsListOfExpression = true;
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner);
                if (expressionArray.length > 0) {
                    setting.preSqls = expressionArray.map((expression) => {
                        if (!expression.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(expression);
                        }
                        return expression;
                    });
                }
            } else {
                setting.isPreSqlsListOfExpression = false;
                setting.preSqls = [
                    DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, ArrayDataFlowType.TYPE)
                ];
            }
            handledConfigs.push('preSQLs');
        }
        if (configs.has('postSQLs')) {
            const expression = configs.get('postSQLs');
            if (expression.startsWith('[') && expression.endsWith(']')) {
                setting.isPostSqlsListOfExpression = true;
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner);
                if (expressionArray.length > 0) {
                    setting.postSqls = expressionArray.map((expression) => {
                        if (!expression.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(expression);
                        }
                        return expression;
                    });
                }
            } else {
                setting.isPostSqlsListOfExpression = false;
                setting.postSqls = [
                    DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, ArrayDataFlowType.TYPE)
                ];
            }
        }
        return setting;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): AdxSettings {
        const result = Owned.create<AdxSettings>(AdxSettings.MODEL, owner);
        result.initialize(undefined);
        return result;
    }
}
